<template>
  <section class="py-20 bg-kannister-dark-50">
    <div class="section-container">
      <!-- Section Header -->
      <div class="text-center mb-16">
        <h2 class="text-4xl md:text-5xl font-bold text-kannister-dark-900 mb-4">
          Enterprise Security, <span class="text-kannister-blue-600">Developer Simplicity</span>
        </h2>
      </div>

      <!-- Architecture Flow Diagram -->
      <div class="max-w-4xl mx-auto mb-16">
        <div class="bg-white rounded-lg p-8 shadow-md">
          <div class="flex flex-col space-y-4">
            <!-- Step 1 -->
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0 w-10 h-10 bg-kannister-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <div class="flex-1">
                <div class="text-lg font-semibold text-kannister-dark-900">Developer writes code</div>
                <div class="text-sm text-kannister-dark-600">Dev Container starts normally</div>
              </div>
              <svg class="w-6 h-6 text-kannister-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            <!-- Step 2 -->
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0 w-10 h-10 bg-kannister-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <div class="flex-1">
                <div class="text-lg font-semibold text-kannister-dark-900">Kannister intercepts</div>
                <div class="text-sm text-kannister-dark-600">Transparent to developer workflow</div>
              </div>
              <svg class="w-6 h-6 text-kannister-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            <!-- Step 3 -->
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0 w-10 h-10 bg-kannister-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                3
              </div>
              <div class="flex-1">
                <div class="text-lg font-semibold text-kannister-dark-900">Security checks run</div>
                <div class="text-sm text-kannister-dark-600">Policy validation + compliance scanning</div>
              </div>
              <svg class="w-6 h-6 text-kannister-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            <!-- Step 4 -->
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0 w-10 h-10 bg-kannister-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                4
              </div>
              <div class="flex-1">
                <div class="text-lg font-semibold text-kannister-dark-900">Audit logs captured</div>
                <div class="text-sm text-kannister-dark-600">Sent to central compliance dashboard</div>
              </div>
              <svg class="w-6 h-6 text-kannister-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            <!-- Step 5 -->
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                ✓
              </div>
              <div class="flex-1">
                <div class="text-lg font-semibold text-kannister-dark-900">Developer continues working</div>
                <div class="text-sm text-kannister-dark-600">No interruption, seamless experience</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Key Points -->
      <div class="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
        <!-- Point 1 -->
        <div class="text-center">
          <div class="w-16 h-16 bg-kannister-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-kannister-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-kannister-dark-900 mb-3">Invisible Integration</h3>
          <p class="text-kannister-dark-600">
            Kannister runs transparently alongside your dev containers. Developers use their familiar tools and workflows—Docker, VS Code, GitHub Codespaces—without any changes.
          </p>
        </div>

        <!-- Point 2 -->
        <div class="text-center">
          <div class="w-16 h-16 bg-kannister-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-kannister-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-kannister-dark-900 mb-3">Policy Enforcement at Runtime</h3>
          <p class="text-kannister-dark-600">
            Define security policies once, enforce everywhere. From network restrictions to credential scanning to compliance requirements, policies apply automatically to every container.
          </p>
        </div>

        <!-- Point 3 -->
        <div class="text-center">
          <div class="w-16 h-16 bg-kannister-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-kannister-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-kannister-dark-900 mb-3">Complete Audit Trail</h3>
          <p class="text-kannister-dark-600">
            Every action is logged. Who accessed what, when, and from where. Full compliance reporting for SOC2, ISO 27001, HIPAA, and custom frameworks.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>
